import React, { useEffect, useMemo, useRef, useState } from 'react'
import Highlight from './Highlight'
import Sidebar from './Sidebar'
import Header from './Header'
import SectionCard from './SectionCard'
import DSASubmissionCard from './DSASubmissionCard'
import Transcript, {
  TranscriptFiltersBar,
  buildFlagOptions,
  type Message as TranscriptMessage,
  type QuestionAsked,
} from './Transcript'
import ProfileHeader from './micro-components/ProfileHeader'
import { TableSection } from './micro-components/TableSection'
import type { Problem, Submission, EvaluationData, InterviewDetails } from '@/api/reportApi'

// Helper to normalize DSA evaluation metrics into table rows
const normalizeEvaluationToRows = (evaluation: EvaluationData | undefined) => {
  if (!evaluation) return []
  
  const rows: Array<{ criteria: string; rating: number | string; remarks: string }> = []
  
  // Code Quality
  if (evaluation.code_quality) {
    const cq = evaluation.code_quality
    rows.push({ criteria: 'Correctness', rating: cq.correctness.rating, remarks: cq.correctness.remarks })
    rows.push({ criteria: 'Efficiency', rating: cq.efficiency.rating, remarks: cq.efficiency.remarks })
    rows.push({ criteria: 'Readability', rating: cq.readability.rating, remarks: cq.readability.remarks })
  }
  
  // Problem Solving
  if (evaluation.problem_solving) {
    const ps = evaluation.problem_solving
    rows.push({ criteria: 'Approach', rating: ps.approach.rating, remarks: ps.approach.remarks })
    rows.push({ criteria: 'Algorithm Choice', rating: ps.algorithm_choice.rating, remarks: ps.algorithm_choice.remarks })
    rows.push({ criteria: 'Optimization', rating: ps.optimization.rating, remarks: ps.optimization.remarks })
  }
  
  // Communication
  if (evaluation.communication) {
    const comm = evaluation.communication
    rows.push({ criteria: 'Explanation', rating: comm.explanation.rating, remarks: comm.explanation.remarks })
    rows.push({ criteria: 'Thought Process', rating: comm.thought_process.rating, remarks: comm.thought_process.remarks })
  }
  
  return rows
}

// Helper to format complexity analysis
const normalizeComplexityToRows = (evaluation: EvaluationData | undefined) => {
  if (!evaluation?.complexity_analysis) return []
  
  const ca = evaluation.complexity_analysis
  return [
    { 
      criteria: 'Time Complexity', 
      rating: ca.time_complexity.rating, 
      remarks: `Stated: ${ca.time_complexity.stated} | Actual: ${ca.time_complexity.actual}` 
    },
    { 
      criteria: 'Space Complexity', 
      rating: ca.space_complexity.rating, 
      remarks: `Stated: ${ca.space_complexity.stated} | Actual: ${ca.space_complexity.actual}` 
    },
  ]
}

interface DSAReportProps {
  report: {
    interviewId?: string
    generationStatus?: string
    problem?: Problem
    submission?: Submission
    evaluation?: {
      status: string
      data?: EvaluationData
      error?: string
    }
    profile?: {
      name?: string
      username?: string
      profile_picture?: string
      profile_url?: string
    }
    interviewDetails?: InterviewDetails
    messages?: TranscriptMessage[]
    s3_recording_url?: string[]
    recording?: {
      mergedUrl?: string | null
      statusOfRecording?: string | null
    }
  } | null
}

const Report: React.FC<DSAReportProps> = ({ report }) => {
  const [activeSection, setActiveSection] = useState<string>('summary')
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [transcriptFlagFilter, setTranscriptFlagFilter] = useState<string>('All')
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState<string>('')

  const profileData = report?.profile
  const hasProfile = Boolean(profileData)
  
  // DSA-specific data
  const problem = report?.problem
  const submission = report?.submission
  const evaluationData = report?.evaluation?.data
  const evaluationStatus = report?.evaluation?.status
  const isEvaluationLoading = evaluationStatus === 'pending' || evaluationStatus === 'in_progress'
  
  // Computed values
  const summaryText = evaluationData?.summary || ''
  const overallRating = evaluationData?.overall_rating
  const strengths = evaluationData?.strengths || []
  const improvements = evaluationData?.improvements || []
  
  // Normalize evaluation data for tables
  const evaluationRows = normalizeEvaluationToRows(evaluationData)
  const complexityRows = normalizeComplexityToRows(evaluationData)
  
  // Transcript data
  const transcriptMessages: TranscriptMessage[] = Array.isArray(report?.messages) ? report.messages : []
  const transcriptQuestions: QuestionAsked[] = []
  const transcriptUsername = profileData?.username || ''
  const hasTranscript = transcriptMessages.length > 0
  const transcriptFlagOptions = useMemo(
    () => ['All', ...buildFlagOptions(transcriptQuestions)],
    [transcriptQuestions],
  )

  // Video
  const videoUrl = report?.recording?.mergedUrl || report?.s3_recording_url?.[0]
  const isVideoProcessing = report?.recording?.statusOfRecording === 'in_progress'

  // Section visibility
  const showSummary = Boolean(hasProfile || summaryText || overallRating !== undefined)
  const showSubmission = Boolean(problem && submission)
  const showVideo = Boolean(videoUrl || isVideoProcessing)
  const showEvaluation = evaluationRows.length > 0
  const showComplexity = complexityRows.length > 0
  const showStrengthsImprovements = strengths.length > 0 || improvements.length > 0
  const showTranscript = hasTranscript

  const navItems = useMemo(
    () =>
      [
        { title: 'Summary', anchor: 'summary', show: showSummary },
        { title: 'Problem & Code', anchor: 'submission', show: showSubmission },
        { title: 'Interview Video', anchor: 'video', show: showVideo },
        { title: 'Evaluation', anchor: 'evaluation', show: showEvaluation || isEvaluationLoading },
        { title: 'Complexity Analysis', anchor: 'complexity', show: showComplexity },
        { title: 'Feedback', anchor: 'feedback', show: showStrengthsImprovements },
        { title: 'Transcript', anchor: 'transcript', show: showTranscript },
      ].filter((item) => item.show),
    [
      showSummary,
      showSubmission,
      showVideo,
      showEvaluation,
      showComplexity,
      showStrengthsImprovements,
      showTranscript,
      isEvaluationLoading,
    ],
  )

  useEffect(() => {
    const firstAnchor = navItems[0]?.anchor
    if (firstAnchor && !navItems.some((item) => item.anchor === activeSection)) {
      setActiveSection(firstAnchor)
    }
  }, [navItems, activeSection])

  useEffect(() => {
    if (!navItems.length) return
    if (observerRef.current) observerRef.current.disconnect()

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]) {
          setActiveSection(visible[0].target.id)
        }
      },
      {
        root: null,
        rootMargin: '-35% 0px -35% 0px',
        threshold: [0.2, 0.4, 0.6, 0.8, 1],
      },
    )

    navItems.forEach((section) => {
      const el = document.getElementById(section.anchor)
      if (el) observer.observe(el)
    })

    observerRef.current = observer
    return () => observer.disconnect()
  }, [navItems])

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveSection(id)
    }
  }

  const mobileNavItems = navItems
  const mobileNavRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  useEffect(() => {
    const activeEl = mobileNavRefs.current[activeSection]
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [activeSection])

  const MobileNav: React.FC = () => (
    <div className="lg:hidden sticky top-[68px] z-30 bg-[#f6f8fb] px-3 pb-2">
      <div className="flex gap-2 overflow-x-auto overflow-y-hidden no-scrollbar pr-1">
        {mobileNavItems.map((item) => {
          const isActive = activeSection === item.anchor
          return (
            <button
              key={item.anchor}
              ref={(el) => {
                mobileNavRefs.current[item.anchor] = el
              }}
              onClick={() => scrollToSection(item.anchor)}
              className={`relative whitespace-nowrap px-4 py-2 text-sm font-medium transition rounded-full border ${
                isActive
                  ? 'bg-[#0F172A] text-white border-[#0F172A]'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              {item.title}
              {isActive ? (
                <span className="absolute left-4 right-4 -bottom-[9px] h-[3px] rounded-full bg-[#0F172A]" />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )

  // Build performance highlights for the Highlight component
  const performanceHighlights = evaluationData ? {
    rating: overallRating,
    category: 'Overall Performance',
    remarks: summaryText,
    items: evaluationRows.slice(0, 4).map(row => ({
      criteria: row.criteria,
      rating: row.rating,
      remarks: row.remarks,
    })),
  } : null

  return (
    <div className="w-full bg-[#f6f8fb] min-h-screen">
      <div className="sticky top-0 z-30 bg-[#f6f8fb]">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3">
          <Header />
        </div>
      </div>

      {mobileNavItems.length ? <MobileNav /> : null}

      <div className="flex flex-col gap-8 sm:gap-5">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 w-full flex flex-col gap-9 md:flex-row md:items-start md:gap-8 py-6">
          <div className="w-full md:w-[25%] hidden md:block lg:sticky lg:top-[88px] lg:self-start lg:max-h-[calc(100vh-110px)] lg:overflow-auto">
            <Sidebar items={navItems} />
          </div>

          <div className="w-full md:w-[75%] flex flex-col gap-6 md:gap-7 min-w-0 h-full">
            {/* Summary Section */}
            {showSummary ? (
              <div id="summary" className="scroll-mt-28 flex flex-col gap-4">
                {hasProfile ? (
                  <ProfileHeader
                    profileData={{
                      profile_picture: profileData?.profile_picture,
                      name: profileData?.name,
                      username: profileData?.username,
                    }}
                  />
                ) : null}
                {performanceHighlights ? (
                  <Highlight
                    performance_highlights={performanceHighlights}
                    summary={summaryText}
                    rating={overallRating}
                  />
                ) : isEvaluationLoading ? (
                  <div className="bg-white rounded-[9px] shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-gray-600">Generating evaluation...</span>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Problem & Submitted Code Section */}
            {showSubmission && problem && submission ? (
              <div id="submission" className="scroll-mt-28">
                <DSASubmissionCard problem={problem} submission={submission} />
              </div>
            ) : null}

            {/* Video Section */}
            {showVideo ? (
              <SectionCard id="video" title="Interview Video">
                {videoUrl ? (
                  <video className="w-full rounded-lg border bg-black max-h-[420px]" controls src={videoUrl} />
                ) : (
                  <div className="text-sm text-gray-500">Video is processing. Check back in a moment.</div>
                )}
              </SectionCard>
            ) : null}

            {/* Evaluation Section */}
            {(showEvaluation || isEvaluationLoading) ? (
              <SectionCard
                id="evaluation"
                title="Performance Evaluation"
              >
                {isEvaluationLoading ? (
                  <div className="flex items-center gap-3 py-4">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-gray-600">Generating detailed evaluation...</span>
                  </div>
                ) : (
                  <TableSection title="Evaluation Metrics" rows={evaluationRows} />
                )}
              </SectionCard>
            ) : null}

            {/* Complexity Analysis Section */}
            {showComplexity ? (
              <SectionCard
                id="complexity"
                title="Complexity Analysis"
              >
                <TableSection title="Time & Space Complexity" rows={complexityRows} />
              </SectionCard>
            ) : null}

            {/* Strengths & Improvements Section */}
            {showStrengthsImprovements ? (
              <SectionCard
                id="feedback"
                title="Strengths & Areas for Improvement"
              >
                <div className="flex flex-col gap-6">
                  {strengths.length > 0 ? (
                    <div>
                      <h4 className="font-dm-sans text-base font-medium text-green-700 mb-3">Strengths</h4>
                      <ul className="list-disc pl-5 space-y-2">
                        {strengths.map((strength: string, idx: number) => (
                          <li key={idx} className="text-sm text-gray-700">{strength}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {improvements.length > 0 ? (
                    <div>
                      <h4 className="font-dm-sans text-base font-medium text-amber-700 mb-3">Areas for Improvement</h4>
                      <ul className="list-disc pl-5 space-y-2">
                        {improvements.map((improvement: string, idx: number) => (
                          <li key={idx} className="text-sm text-gray-700">{improvement}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </SectionCard>
            ) : null}

            {/* Transcript Section */}
            {showTranscript ? (
              <SectionCard
                id="transcript"
                title="Interview Transcript"
                actions={
                  hasTranscript ? (
                    <TranscriptFiltersBar
                      flagFilter={transcriptFlagFilter}
                      onFlagFilterChange={setTranscriptFlagFilter}
                      filterOptions={transcriptFlagOptions}
                      searchTerm={transcriptSearchTerm}
                      onSearchTermChange={setTranscriptSearchTerm}
                    />
                  ) : null
                }
              >
                {hasTranscript ? (
                  <Transcript
                    questionsAsked={transcriptQuestions}
                    messages={transcriptMessages}
                    username={transcriptUsername}
                    isSharedReport={false}
                    hideControls
                    flagFilter={transcriptFlagFilter}
                    onFlagFilterChange={setTranscriptFlagFilter}
                    searchTerm={transcriptSearchTerm}
                    onSearchTermChange={setTranscriptSearchTerm}
                    filterOptionsOverride={transcriptFlagOptions.slice(1)}
                  />
                ) : null}
              </SectionCard>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Report
